import type { ReactNode } from "react";
import { Ban, Image as ImageIcon } from "lucide-react";
import type { MdcpPhotoPoint, MdcpPointPhoto } from "../types";
import { formatCoords2000, puwgZoneLabel } from "./mdcpMap";

/** Wspólne klocki sekcji „Punkty zdjęciowe MDCP" — ten sam wygląd u zamawiającego
 *  (MdcpPointsPanel) i u geodety (MdcpContractorMap). Strony różnią się wyłącznie akcjami
 *  (odbiór zdjęć i ZIP u nas, aparat/galeria u geodety), które wpinają się przez
 *  render-propsy; układ, nagłówek punktu, siatka zdjęć, lista punktów ze współrzędnymi
 *  i stopka o układzie współrzędnych są jedne — geodeta ma widzieć dokładnie to, co my. */

/** „Punkt #N · azymut X°" + dokładne współrzędne PUWG + notatka. */
export function MdcpSelectedPointHeader({ point }: { point: MdcpPhotoPoint }) {
  return (
    <>
      <div className="flex items-center gap-2 text-sm flex-wrap">
        <span className="font-mono font-semibold">Punkt #{point.number}</span>
        <span className="text-[var(--color-text-muted)]">· azymut {point.azimuth_deg}°</span>
      </div>
      {point.coords_2000 && (
        <p className="font-mono text-[11px] text-[var(--color-text-secondary)]">
          {formatCoords2000(point.coords_2000)}
        </p>
      )}
      {point.note && <p className="text-xs text-[var(--color-text-secondary)]">{point.note}</p>}
    </>
  );
}

/** Miniatury zdjęć punktu (klik = lightbox). Zdjęcie odrzucone przy odbiorze zostaje na
 *  widoku wyszarzone, razem z powodem — bez tego geodeta wróciłby w teren, nie wiedząc,
 *  co ma poprawić. */
export function MdcpPointPhotoGrid({
  photos,
  onOpen,
  overlay,
}: {
  photos: MdcpPointPhoto[];
  onOpen: (photo: MdcpPointPhoto) => void;
  /** Przyciski w rogu miniatury (usuń / odrzuć / przywróć) — zależne od strony; brak = podgląd. */
  overlay?: (photo: MdcpPointPhoto) => ReactNode;
}) {
  if (photos.length === 0) {
    return <p className="text-[11px] text-[var(--color-text-muted)]">Brak zdjęć dla tego punktu.</p>;
  }
  return (
    <div className="grid grid-cols-2 gap-2">
      {photos.map((ph) => (
        <div key={ph.id} className="relative group space-y-0.5">
          <button
            type="button"
            title={ph.original_name}
            onClick={() => onOpen(ph)}
            className="block w-full cursor-zoom-in"
          >
            <img
              src={ph.file?.url ?? undefined}
              alt={ph.original_name}
              className={`h-24 w-full object-cover border ${
                ph.is_rejected
                  ? "border-[var(--color-primary)] opacity-45 grayscale"
                  : "border-[var(--color-border)]"
              }`}
            />
          </button>
          {overlay && <div className="absolute top-0.5 right-0.5 flex gap-0.5">{overlay(ph)}</div>}
          {ph.is_rejected && (
            <p className="text-[10px] leading-tight text-[var(--color-primary)]">
              <span className="font-semibold">Odrzucone</span>
              {ph.rejected_by_name ? ` · ${ph.rejected_by_name}` : ""}
              {ph.rejection_reason ? `: ${ph.rejection_reason}` : ""}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

/** Lista punktów pod mapą: numer, azymut, współrzędne PUWG, notatka, liczniki zdjęć.
 *  Klik w wiersz wybiera punkt (i przesuwa mapę). */
export function MdcpPointsList({
  points,
  selectedId,
  onSelect,
  rowAction,
}: {
  points: MdcpPhotoPoint[];
  selectedId: string | null;
  onSelect: (point: MdcpPhotoPoint) => void;
  /** Akcja na końcu wiersza (kompaktowe „Wgraj"); brak = wiersz tylko do wyboru. */
  rowAction?: (point: MdcpPhotoPoint) => ReactNode;
}) {
  return (
    <ul className="space-y-1">
      {points.map((p) => (
        <li
          key={p.id}
          className={`flex items-center gap-3 rounded border px-2 py-1 text-xs cursor-pointer transition-colors ${
            selectedId === p.id
              ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
              : "border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)]"
          }`}
          onClick={() => onSelect(p)}
        >
          <span className="font-mono font-semibold shrink-0">#{p.number}</span>
          <span className="font-mono text-[var(--color-text-muted)] shrink-0">{p.azimuth_deg}°</span>
          <span className="font-mono text-[11px] text-[var(--color-text-secondary)] shrink-0">
            {formatCoords2000(p.coords_2000) ?? "brak współrzędnych"}
          </span>
          {p.note && (
            <span className="truncate text-[var(--color-text-secondary)]" title={p.note}>
              {p.note}
            </span>
          )}
          <span className="ml-auto" />
          {p.photos_count > 0 && (
            <span className="inline-flex items-center gap-0.5 shrink-0 text-green-600">
              <ImageIcon size={12} />
              {p.photos_count}
            </span>
          )}
          {p.rejected_photos_count > 0 && (
            <span
              className="inline-flex items-center gap-0.5 shrink-0 text-[var(--color-primary)]"
              title={`${p.rejected_photos_count} odrzuconych zdjęć w tym punkcie`}
            >
              <Ban size={12} />
              {p.rejected_photos_count}
            </span>
          )}
          {rowAction?.(p)}
        </li>
      ))}
    </ul>
  );
}

/** Stopka: konwencja azymutu, skąd trasa/działki i w jakim układzie są współrzędne. */
export function MdcpPointsFootnote({ points }: { points: MdcpPhotoPoint[] }) {
  const srid = points.find((p) => p.coords_2000)?.coords_2000?.srid;
  return (
    <p className="text-[11px] text-[var(--color-text-muted)]">
      Kierunek zdjęcia: azymut od północy, zgodnie z ruchem wskazówek. Trasa (czerwona) i
      działki — aktualna wersja z mapy projektu ZE.
      {srid ? ` Współrzędne punktów: X (północ) / Y (wschód), ${puwgZoneLabel(srid)} [m].` : ""}
    </p>
  );
}
